/**
 * Promise.withResolvers landed in Safari 17.4. pdfjs-dist 5.x (via react-pdf v10)
 * calls it in both the main thread and the worker; without a polyfill, Lesson
 * Materials PDF viewing crashes on older Safari / iPadOS.
 */

type PromiseWithResolversResult<T> = {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
};

type PromiseWithResolversCapable = PromiseConstructor & {
  withResolvers?: <T>() => PromiseWithResolversResult<T>;
};

/** Source string for injecting the same polyfill into a module worker bootstrap. */
export const PROMISE_WITH_RESOLVERS_POLYFILL_SOURCE = `if (typeof Promise.withResolvers !== "function") {
  Promise.withResolvers = function withResolvers() {
    var resolve, reject;
    var promise = new Promise(function (res, rej) {
      resolve = res;
      reject = rej;
    });
    return { promise: promise, resolve: resolve, reject: reject };
  };
}
`;

export function applyPromiseWithResolversPolyfill(
  target: Pick<typeof globalThis, "Promise"> = globalThis,
): void {
  const PromiseCtor = target.Promise as PromiseWithResolversCapable;
  if (typeof PromiseCtor.withResolvers === "function") {
    return;
  }

  PromiseCtor.withResolvers = function withResolvers<T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

applyPromiseWithResolversPolyfill();
